import { Loader2, ShieldAlert, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { to55, regraPorTipoLinha, conflitoOperadora } from '@/lib/operadoraCoerencia';
import type { OperadoraLinha } from '@/hooks/useOperadoraLinhas';

// ============================================================================
// COERÊNCIA PRODUTO × OPERADORA — peças de UI (Douglas 07/08).
//
// `SeloOperadoraLinha` vive DENTRO do quadrante de cada linha do Montador,
// logo abaixo do número — era uma faixa separada no rodapé e o Douglas vetou:
// "lugar errado, com dificuldade de entendimento; junto do quadrante, sutil".
// `conflitosDaComposicao` é o gate que trava Recuperar/Abrir Pedido no modal.
// ============================================================================

export interface LinhaConferencia {
  id?: string;
  tipo?: string | null;
  numero?: string | null;
}

export function conflitosDaComposicao(
  linhas: LinhaConferencia[],
  porTelefone: Map<string, OperadoraLinha>,
): string[] {
  const problemas: string[] = [];
  for (const l of linhas || []) {
    const regra = regraPorTipoLinha(l?.tipo);
    const t = to55(l?.numero);
    if (!regra || !t) continue;
    const op = porTelefone.get(t)?.operadora;
    const msg = conflitoOperadora(regra, op);
    if (msg) problemas.push(`${String(l?.numero)}: ${msg}`);
  }
  return problemas;
}

/**
 * Selo sutil da operadora, por linha. Só aparece quando o tipo tem régua
 * (PORTABILIDADE/TT/RENOVAÇÃO) e o número está completo. Verde = coerente,
 * vermelho = conflito confirmado (com a mensagem), spinner enquanto consulta.
 * Sem consulta = não renderiza nada (não polui e não bloqueia).
 */
export function SeloOperadoraLinha({ tipo, numero, porTelefone, consultando }: {
  tipo?: string | null;
  numero?: string | null;
  porTelefone: Map<string, OperadoraLinha>;
  consultando: boolean;
}) {
  const regra = regraPorTipoLinha(tipo);
  const t = to55(numero);
  if (!regra || !t) return null;

  const op = porTelefone.get(t)?.operadora;
  const msg = conflitoOperadora(regra, op);

  if (!op) {
    return consultando ? (
      <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> consultando operadora…
      </span>
    ) : null;
  }

  return (
    <span
      title={msg ?? `Consulta oficial: ${op} — coerente com ${String(tipo).toUpperCase()}`}
      className={cn(
        'inline-flex items-center gap-1 text-[10px] font-medium',
        msg ? 'text-destructive font-bold' : 'text-success',
      )}
    >
      {msg ? <ShieldAlert className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />}
      {op}
      {msg && <span className="font-medium">— {msg}</span>}
    </span>
  );
}
